import { scopedProjectKey } from "@cmmd-forge/client-runtime";
import type {
  ModelSelection,
  ProviderInteractionMode,
  RuntimeMode,
  ScopedProjectRef,
} from "@cmmd-forge/contracts";

import {
  useComposerDraftStore,
  type ComposerFileAttachment,
  type ComposerImageAttachment,
} from "../../composerDraftStore";
import { deriveLogicalProjectKey } from "../../logicalProject";
import type { TerminalContextDraft } from "../../lib/terminalContext";
import type { Project } from "../../types";

export interface NewThreadDraftHydrationInput {
  readonly projectRef: ScopedProjectRef;
  readonly targetProject: Project;
  readonly modelSelection: ModelSelection;
  readonly interactionMode: ProviderInteractionMode;
  readonly runtimeMode: RuntimeMode;
  readonly prompt: string;
  readonly images: ReadonlyArray<ComposerImageAttachment>;
  readonly files?: ReadonlyArray<ComposerFileAttachment>;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly onImagesTransferred?:
    | ((images: ReadonlyArray<ComposerImageAttachment>) => void)
    | undefined;
}

export function hydrateCreatedDraft(input: NewThreadDraftHydrationInput): boolean {
  const draftStore = useComposerDraftStore.getState();
  const scopedKey = scopedProjectKey(input.projectRef);
  const draft = [deriveLogicalProjectKey(input.targetProject), scopedKey]
    .map((logicalKey) => draftStore.getDraftSessionByLogicalProjectKey(logicalKey))
    .find((candidate) => candidate !== null);
  if (draft === undefined) return false;

  draftStore.setModelSelection(draft.draftId, input.modelSelection);
  draftStore.setDraftThreadContext(draft.draftId, {
    interactionMode: input.interactionMode,
    runtimeMode: input.runtimeMode,
  });
  if (input.images.length > 0) {
    draftStore.addImages(draft.draftId, [...input.images]);
    input.onImagesTransferred?.(input.images);
  }
  if (input.files?.length) draftStore.addFiles(draft.draftId, [...input.files]);
  if (input.terminalContexts.length > 0) {
    // A /new handoff is already committed to the created draft before ChatView
    // sends it. Keep the selected terminal text through a reload in that gap.
    draftStore.setTerminalContexts(draft.draftId, [...input.terminalContexts], {
      preserveTextForReload: true,
    });
  }
  if (input.prompt.length > 0) {
    draftStore.setPrompt(draft.draftId, input.prompt);
  }
  if (
    input.prompt.length === 0 &&
    input.images.length === 0 &&
    (input.files?.length ?? 0) === 0 &&
    input.terminalContexts.length === 0
  )
    return true;
  draftStore.setPendingAutoSubmit(draft.draftId, true);
  return true;
}
